'use client';

import React from 'react';
import { observer, Memo } from '@legendapp/state/react';
import { observable, type Observable } from '@legendapp/state';
import type { GiftItemProps } from './gift-item.types';
import type { SelectedGift, GiftProductItem } from '../../../types/gift-selection.types';
import type { FreeGiftsData, Rule } from '../../../../../types/front.graphql';
import styles from './gift-item.module.css';
import { Text } from '../../../../../utils';
import { Radio } from '@kidslabs/mario-nextjs-react-ui-core';
import { formatPriceSimple } from '@kidslabs/module-catalog';
import { GiftQuantityBadge } from '../gift-quantity-badge';

// Calculate gift eligibility based on rule - SAME as useGiftSelection hook
const calculateGiftEligibility = (rule: Rule, quantity: number) => {
    const buyX = rule.discount_step || 1; // Buy X quantity
    const getY = rule.discount_amount || 1; // Get Y free gifts

    const eligibleSets = Math.floor(quantity / buyX);
    const maxFreeGifts = eligibleSets * getY;
    const quantityUsedByRule = eligibleSets * buyX; // Quantity consumed by this rule

    return {
        canGetGifts: eligibleSets > 0,
        maxFreeGifts,
        requiredQuantity: buyX,
        buyQuantity: quantity,
        quantityUsedByRule,
    };
};

const getGiftQuantity = ({
    currentSelectedGifts,
    giftItem,
    giftData,
    productQuantity,
    selectionRound = 1,
    highlighted = false,
}: {
    currentSelectedGifts: SelectedGift[];
    giftItem: GiftProductItem;
    giftData: FreeGiftsData;
    productQuantity: number;
    selectionRound?: number;
    highlighted?: boolean;
}) => {
    // Ensure currentSelectedGifts is an array (null safety)
    const safeSelectedGifts = currentSelectedGifts ?? [];

    // When highlighted (rendering in "Quà đã chọn" section), skip selectionRound
    // matching because gifts from different rounds are displayed together and
    // selectionRound$ is not passed from SelectedGifts component.
    const selectedGift = safeSelectedGifts.find(
        g =>
            g.productId === giftItem?.id &&
            g.ruleId === giftData?.rule?.rule_id &&
            (highlighted || g.selectionRound === selectionRound)
    );

    if (selectedGift) {
        return selectedGift.quantity;
    }

    // For cart-level gifts: use max_qty from the gift product if available.
    // max_qty represents how many of this specific gift the customer can receive.
    if (giftItem.maxQuantity != null && giftItem.maxQuantity > 0) {
        return giftItem.maxQuantity;
    }

    // Fallback: calculate potential quantity using rule-based logic (product-level gifts)
    const rule = giftData?.rule;
    if (!rule) return 1;

    const eligibility = calculateGiftEligibility(rule, productQuantity);
    return eligibility.maxFreeGifts;
};

const isGiftCurrentlySelected = (
    selectedGifts$: Observable<SelectedGift[]>,
    giftItem: GiftProductItem,
    giftData: FreeGiftsData,
    selectionRound: number
): boolean => {
    // Ensure selectedGifts is an array (null safety)
    const selectedGifts = selectedGifts$?.get() ?? [];
    const ruleId = giftData?.rule?.rule_id;
    const productId = giftItem?.id;

    // Check by productId + ruleId + selectionRound for accurate matching
    return selectedGifts.some(
        g => g.productId === productId && g.ruleId === ruleId && g.selectionRound === selectionRound
    );
};

export const GiftItem = observer((props: GiftItemProps) => {
    const {
        giftData,
        giftItem,
        containerClassName,
        itemDisabled$,
        highlighted,
        selectedGifts$,
        productQuantity$,
        selectionRound$,
        onSelectGift = () => {},
        sessionId,
        enableAutoFill,
        onAutoFillRequired,
    } = props;
    // ✅ FIX: Handle undefined observables with defaults
    if (!selectedGifts$) {
        return null;
    }

    // Create default observables if not provided
    const productQuantityObs$ = productQuantity$ ?? observable(1);
    const selectionRoundObs$ = selectionRound$ ?? observable(1);

    return (
        <Memo>
            {(): React.ReactElement => {
                // All reactive - Legend State tracks these observables
                // Ensure selectedGifts is an array (null safety)
                const selectedGifts = selectedGifts$.get() ?? [];
                const itemDisabled = itemDisabled$?.get() ?? false;
                const productQuantity = productQuantityObs$.get();
                const selectionRound = selectionRoundObs$.get();

                const giftQuantity = getGiftQuantity({
                    currentSelectedGifts: selectedGifts,
                    giftItem,
                    giftData,
                    productQuantity: productQuantity,
                    selectionRound: selectionRound,
                    highlighted,
                });

                const onPressHandler = () => {
                    // Legacy: block when disabled and no auto-fill
                    if (itemDisabled && !enableAutoFill) return;

                    // Create new selected gift with selectionRound
                    const newSelectedGift: SelectedGift = {
                        id: `${giftData?.rule?.rule_id}-${giftItem?.id}-${selectionRound}`,
                        ruleId: giftData?.rule?.rule_id ?? 0,
                        productId: giftItem?.id ?? 0,
                        selectionRound: selectionRound,
                        item: giftItem,
                        giftData: giftData,
                        quantity: giftQuantity,
                        selectedAt: Date.now(),
                        giftSlots: giftQuantity,
                        sessionId,
                    };

                    if (itemDisabled && enableAutoFill) {
                        // Auto-fill: trigger parent to update quantity then select
                        const requiredQuantity = giftData?.rule?.discount_step ?? 1;
                        onAutoFillRequired?.(requiredQuantity, newSelectedGift);
                        return;
                    }

                    onSelectGift(newSelectedGift);
                };

                const numberOfItems = giftData?.rule?.discount_amount ?? giftQuantity;
                const isSelected = isGiftCurrentlySelected(selectedGifts$, giftItem, giftData, selectionRound);

                // Build class names based on state
                const containerClasses = [
                    styles.itemContainer,
                    itemDisabled && !enableAutoFill && styles.disabled,
                    highlighted && styles.highlighted,
                    containerClassName,
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <div className={containerClasses} data-testid="gift-option-item">
                        <div
                            className={styles.checkboxContainer}
                            style={{ pointerEvents: highlighted ? 'none' : 'auto' }}
                        >
                            <GiftQuantityBadge quantity={giftQuantity} />
                            <Radio
                                checked={isSelected || highlighted}
                                disabled={enableAutoFill ? false : itemDisabled}
                                onChange={highlighted ? undefined : onPressHandler}
                                size="medium"
                                allowUncheck
                                data-testid="gift-option-item-radio"
                            />
                        </div>
                        <div className={styles.giftContentWrapper}>
                            {Array.from({ length: numberOfItems }).map((_, idx) => {
                                const key = giftItem?.id + '-' + giftData?.rule?.rule_id + '-' + idx;
                                const priceRange = giftItem?.price_range;
                                const priceGift =
                                    priceRange?.maximum_price?.final_price?.value ??
                                    priceRange?.minimum_price?.final_price?.value ??
                                    0;
                                return (
                                    <div className={styles.content} key={key}>
                                        <div className={styles.viewImage}>
                                            <img
                                                src={giftItem?.image?.url || giftItem?.small_image?.url || ''}
                                                alt={giftItem?.name ?? 'Gift product'}
                                                className={styles.image}
                                            />
                                        </div>
                                        <div className={styles.viewName}>
                                            <Text size="small" className={styles.giftName}>
                                                {giftItem?.name}
                                            </Text>
                                            <Text size="small" className={styles.giftPrice}>
                                                {formatPriceSimple(priceGift)}
                                            </Text>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }}
        </Memo>
    );
});
